import React from 'react';
import { useState, useEffect } from 'react';
import { useFormik } from 'formik';
import { TextField, FormControlLabel, Checkbox, Box, Typography } from '@mui/material';
import { Step1Schema } from '../../validations/user';
import PrimaryBlueButton from '../common/buttons/PrimaryBlueButton';
import BorderedButton from '../common/buttons/BorderedButton';
import { Link, useNavigate } from 'react-router-dom';
import { useThemeContext } from '../../contexts/BaseThemeContextProvider';
import { useSearchParams } from 'react-router-dom';
import GradientCircularProgress from '../common/GradientCircularProgress';

const app_co = process.env.REACT_APP_COMPANY;

type InitialStepValues = {
  email: string;
  firstName: string;
  lastName: string;
  tos_flag: boolean;
  marketing_flag: boolean;
  sms_flag: boolean;
  hipaa_npp_flag: boolean;
  hipaa_copy_req_flag: boolean;
};

type Step1Props = {
  onSubmit: (values: any, { setSubmitting }: any) => void;
  isSubmitting: boolean;
  initialFormValues?: InitialStepValues;
  disabledFields?: (keyof InitialStepValues)[];
  cancelButtonText?: string;
  fetchAppointmentDetails: (token: string, setApptDetailsNotFound: any, setLoading: any) => any;
  fetchClientBookingDetails: (token: string, setClientBookingDetailsNotFound: any, setLoading: any) => any;
  fetchInviteDetails?: (inviteToken: string, setInviteNotFound: any, setLoading: any) => any;
};

function splitName(fullName: string): { firstName: string; lastName: string } {
  const parts = fullName.trim().split(/\s+/);

  if (parts.length === 0) {
    return { firstName: '', lastName: '' };
  }

  const firstName = parts[0];
  const lastName = parts.length > 1 ? parts.slice(1).join(' ') : '';

  return { firstName, lastName };
}

const SignupStep1AccountDetails: React.FC<Step1Props> = ({
  onSubmit,
  isSubmitting,
  initialFormValues = {} as InitialStepValues,
  disabledFields = [],
  cancelButtonText = 'Cancel',
  fetchAppointmentDetails,
  fetchClientBookingDetails,
  fetchInviteDetails,
}) => {
  const { theme } = useThemeContext();
  const formik = useFormik({
    validateOnMount: true,
    initialValues: {
      email: initialFormValues.email || '',
      firstName: initialFormValues.firstName || '',
      lastName: initialFormValues.lastName || '',
      tos_flag: initialFormValues.tos_flag || false,
      marketing_flag: initialFormValues.marketing_flag || false,
      sms_flag: initialFormValues.sms_flag || false,
      hipaa_npp_flag: initialFormValues.hipaa_npp_flag || false,
      hipaa_copy_req_flag: initialFormValues.hipaa_copy_req_flag || false,
    },
    validationSchema: Step1Schema,
    onSubmit: onSubmit,
  });
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [apptDetailsNotFound, setApptDetailsNotFound] = useState(true);
  const [clientBookingDetailsfound, setClientBookingDetailsNotFound] = useState(true);
  const [inviteNotFound, setInviteNotFound] = useState(false);
  const [isInviteFlow, setIsInviteFlow] = useState(false);
  const [loading, setLoading] = useState(true);
  const [company, setCompany] = useState('Your Company');

  useEffect(() => {
    const bookingToken = searchParams.get('token');
    const inviteToken = searchParams.get('invite_token');

    if (!bookingToken && !inviteToken) {
      setLoading(false);
      return;
    }

    const noopSetLoading = () => {};

    const fetchData = async () => {
      try {
        if (bookingToken) {
          const clientBookingDetails = await fetchClientBookingDetails(
            bookingToken,
            setClientBookingDetailsNotFound,
            noopSetLoading,
          );

          if (clientBookingDetails?.body?.company) {
            setCompany(clientBookingDetails.body.company);
          }
        }

        if (inviteToken && fetchInviteDetails) {
          const inviteResponse = await fetchInviteDetails(inviteToken, setInviteNotFound, noopSetLoading);
          if (inviteResponse && !inviteResponse.isError) {
            const invitePayload = inviteResponse.body?.invite;
            if (invitePayload?.email) {
              formik.setFieldValue('email', invitePayload.email);
            }
            if (inviteResponse.body?.organization?.name) {
              setCompany(inviteResponse.body.organization.name);
            }
            setIsInviteFlow(true);
          }
        }
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [searchParams]);

  const handleNextStep = () => {
    formik.handleSubmit();
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          textAlign: 'center',
        }}
      >
        <GradientCircularProgress loadingMessage="Loading ..." />
      </Box>
    );
  }
  return (
    <Box
      sx={{
        mt: 1,
        mb: 3,
        padding: { xs: '0 24px', sm: '0 24px', md: '0' },
      }}
    >
      <Typography
        variant="h4"
        gutterBottom
        sx={{ color: theme.custom.components.signupStep1AccountDetails.typography.color }}
      >
        {app_co === 'rh' ? 'Create Your Account' : 'Personal details'}
      </Typography>
      <Typography
        variant="body2"
        sx={{ mb: { xs: 0, md: 2 }, color: theme.custom.components.signupStep1AccountDetails.typography.color }}
      >
        {app_co === 'rh'
          ? 'Please enter the following information to complete your signup.'
          : company +
            ' has contracted FROST to perform respiratory testing. As part of the test, you need to complete certain OSHA-mandated forms. Registering allows you to do this in a secure manner.'}
      </Typography>
      {inviteNotFound && (
        <Typography variant="body2" color="error" sx={{ mb: 2 }}>
          Invite link is invalid or has already been used.
        </Typography>
      )}
      <Box
        sx={{
          [theme.breakpoints.up('md')]: {
            padding: '0px',
            display: 'flex',
            flexWrap: 'wrap',
          },
        }}
      >
        <TextField
          sx={{
            [theme.breakpoints.up('md')]: {
              margin: '10px 10px 10px 0',
              width: '91%',
            },
          }}
          fullWidth
          margin="normal"
          label="Email"
          disabled={disabledFields.includes('email') || isInviteFlow}
          required
          autoComplete="email"
          placeholder="email@example.com"
          {...formik.getFieldProps('email')}
          error={formik.touched.email && Boolean(formik.errors.email)}
          helperText={formik.touched.email && formik.errors.email}
        />
      </Box>
      <Box
        sx={{
          [theme.breakpoints.up('md')]: {
            padding: '0px',
            display: 'flex',
            flexWrap: 'wrap',
          },
        }}
      >
        <TextField
          sx={{
            [theme.breakpoints.up('md')]: {
              margin: '10px 10px 10px 0',
              width: '45%',
            },
          }}
          fullWidth
          margin="normal"
          required
          disabled={disabledFields.includes('firstName')}
          label="First Name"
          placeholder="First Name"
          {...formik.getFieldProps('firstName')}
          error={formik.touched.firstName && Boolean(formik.errors.firstName)}
          helperText={formik.touched.firstName && formik.errors.firstName}
        />
        <TextField
          sx={{
            [theme.breakpoints.up('md')]: {
              margin: '10px 10px 10px 0',
              width: '45%',
            },
          }}
          fullWidth
          margin="normal"
          required
          disabled={disabledFields.includes('lastName')}
          label="Last Name"
          {...formik.getFieldProps('lastName')}
          error={formik.touched.lastName && Boolean(formik.errors.lastName)}
          helperText={formik.touched.lastName && formik.errors.lastName}
        />
      </Box>
      <Typography
        variant="h5"
        gutterBottom
        sx={{ mt: 2, mb: 2, color: theme.custom.components.signupStep1AccountDetails.typography.color }}
      >
        Terms of Service
      </Typography>
      <FormControlLabel
        sx={{
          alignItems: 'flex-start',
          '& .MuiCheckbox-root': {
            padding: '3px 8px',
          },
        }}
        control={<Checkbox {...formik.getFieldProps('tos_flag')} checked={formik.values.tos_flag} />}
        label={
          <Box>
            <Typography
              variant="body2"
              component="div"
              sx={{ color: theme.custom.components.signupStep1AccountDetails.typography.color }}
            >
              Terms of Service and Privacy *
            </Typography>
            <Typography variant="body2" color={theme.custom.components.signupStep1AccountDetails.typography.color}>
              I agree to the
              <Link
                style={{
                  color: theme.custom.components.signupStep1AccountDetails.typography.secondary.color,
                  textDecoration: 'none',
                }}
                to="/tos.html"
                target="blank"
              >
                {' '}
                Terms of Service{' '}
              </Link>
              and have read and understood the
              <Link
                style={{
                  color: theme.custom.components.signupStep1AccountDetails.typography.secondary.color,
                  textDecoration: 'none',
                }}
                to="/privacy-policy.html"
                target="blank"
              >
                {' '}
                Privacy Policy.
              </Link>
            </Typography>
          </Box>
        }
      />
      {formik.touched.tos_flag && formik.errors.tos_flag && (
        <Box sx={{ color: 'error.main' }}>{formik.errors.tos_flag}</Box>
      )}
      <FormControlLabel
        sx={{
          mt: 2,
          mb: 2,
          alignItems: 'flex-start',
          '& .MuiCheckbox-root': {
            padding: '3px 8px',
          },
        }}
        control={<Checkbox {...formik.getFieldProps('marketing_flag')} checked={formik.values.marketing_flag} />}
        label={
          <Box>
            <Typography
              variant="body2"
              component="div"
              color={theme.custom.components.signupStep1AccountDetails.typography.color}
            >
              Marketing
            </Typography>
            <Typography variant="body2" color={theme.custom.components.signupStep1AccountDetails.typography.color}>
              I agree to receive occasional updates, offers, and promotions by email or SMS. Marketing preferences can
              be set in the App Settings page under your Profile. Up to 2 messages per day. Message and data rates may
              apply.
            </Typography>
          </Box>
        }
      />
      {formik.touched.marketing_flag && formik.errors.marketing_flag && (
        <Box sx={{ color: 'error.main' }}>{formik.errors.marketing_flag}</Box>
      )}
      {app_co === 'rh' && (
        <>
          <FormControlLabel
            sx={{
              mt: 0,
              mb: 2,
              alignItems: 'flex-start',
              '& .MuiCheckbox-root': {
                padding: '3px 8px',
              },
            }}
            control={<Checkbox {...formik.getFieldProps('sms_flag')} checked={formik.values.sms_flag} />}
            label={
              <Box>
                <Typography
                  variant="body2"
                  component="div"
                  color={theme.custom.components.signupStep1AccountDetails.typography.color}
                >
                  SMS texts
                </Typography>
                <Typography variant="body2" color={theme.custom.components.signupStep1AccountDetails.typography.color}>
                  I agree to get SMS from Relentless Health for updates, results, reminders, offers, one-time-passcodes
                  (OTP), etc. Up to 2 messages per day, plus OTPs for Sign In. Message and data rates may apply. Reply
                  STOP to opt out or HELP for more information. View our{' '}
                  <Link
                    style={{
                      color: theme.custom.components.signupStep1AccountDetails.typography.secondary.color,
                      textDecoration: 'none',
                    }}
                    to="/tos.html"
                    target="blank"
                  >
                    {' '}
                    Terms of Service{' '}
                  </Link>
                  and{' '}
                  <Link
                    style={{
                      color: theme.custom.components.signupStep1AccountDetails.typography.secondary.color,
                      textDecoration: 'none',
                    }}
                    to="/privacy-policy.html"
                    target="blank"
                  >
                    {' '}
                    Privacy Policy
                  </Link>
                  .
                </Typography>
              </Box>
            }
          />
          {formik.touched.sms_flag && formik.errors.sms_flag && (
            <Box sx={{ color: 'error.main' }}>{formik.errors.sms_flag}</Box>
          )}
        </>
      )}
      {app_co === 'frost' && (
        <>
          <FormControlLabel
            sx={{
              mt: 2,
              mb: 2,
              alignItems: 'flex-start',
              '& .MuiCheckbox-root': {
                padding: '3px 8px',
              },
            }}
            control={<Checkbox {...formik.getFieldProps('hipaa_npp_flag')} checked={formik.values.hipaa_npp_flag} />}
            label={
              <Box>
                <Typography variant="body2" color={theme.custom.components.signupStep1AccountDetails.typography.color}>
                  FROST is an occupational healthcare provider assessing your suitability for employment in high risk
                  roles. As part of the assessment, FROST may access your medical records and history. All data is
                  managed in accordance with the HIPAA Notice of Privacy Practices, which will be emailed after you
                  complete your registration. Please check the box to acknowledge you have read this information. *
                </Typography>
              </Box>
            }
          />
          {formik.touched.hipaa_npp_flag && formik.errors.hipaa_npp_flag && (
            <Box sx={{ color: 'error.main' }}>{formik.errors.hipaa_npp_flag}</Box>
          )}
        </>
      )}
      {app_co === 'frost' && (
        <>
          <FormControlLabel
            sx={{
              mt: 2,
              mb: 2,
              alignItems: 'flex-start',
              '& .MuiCheckbox-root': {
                padding: '0px 8px',
              },
            }}
            control={
              <Checkbox {...formik.getFieldProps('hipaa_copy_req_flag')} checked={formik.values.hipaa_copy_req_flag} />
            }
            label={
              <Box>
                <Typography variant="body2" color={theme.custom.components.signupStep1AccountDetails.typography.color}>
                  Please check this box if you would like to receive a paper copy of the HIPAA Notice of Privacy
                  Practices
                </Typography>
              </Box>
            }
          />
          {formik.touched.hipaa_copy_req_flag && formik.errors.hipaa_copy_req_flag && (
            <Box sx={{ color: 'error.main' }}>{formik.errors.hipaa_copy_req_flag}</Box>
          )}
        </>
      )}
      <Box
        sx={{
          mt: 2,
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          justifyContent: { xs: 'center', sm: 'flex-start' },
          maxWidth: { xs: '100%', sm: 'auto' },
          padding: { xs: '0 24px', sm: '0' },
        }}
      >
        <PrimaryBlueButton
          isLoading={isSubmitting}
          onClick={handleNextStep}
          buttonText="Next"
          additionalProps={{
            disabled: !formik.isValid || isSubmitting,
            style: { marginBottom: '10px' },
          }}
        />
        <BorderedButton
          sxProps={{
            marginLeft: { xs: '0', sm: '10px' },
          }}
          additionalProps={{
            style: { height: '41px' },
          }}
          onClick={() => navigate('/')}
          buttonText={cancelButtonText}
        />
      </Box>
    </Box>
  );
};

export default SignupStep1AccountDetails;
